import type { MapboxTokenDto } from '@signalvote/shared';
import { useState, useEffect } from 'react';
import { API_BASE_URL, SUBSIDIARY_CODE } from '../config';

export interface UseMapboxTokenReturn {
  token: string;
  isLoading: boolean;
  error: string | null;
}

/**
 * Where the Mapbox token comes from:
 *  - `authenticated` (default): the admin token from the JWT-guarded
 *    `/api/configuration/mapbox-token`. Every logged-in admin surface uses this.
 *  - `public`: the RESTRICTED public token from the unauthenticated
 *    `/api/public/mapbox-token/{subsidiary}` route. This
 *    is the seam the anonymous donor page consumes — the subsidiary rides in the
 *    route (no X-Subsidiary header, no bearer). Never returns the admin token.
 */
export type MapboxTokenSource = 'authenticated' | 'public';

export interface UseMapboxTokenOptions {
  /** Whether to fetch the token (useful for lazy loading). Default true. */
  enabled?: boolean;
  /** Which endpoint sources the token. Default `authenticated`. */
  source?: MapboxTokenSource;
  /**
   * Subsidiary code for the `public` route. Defaults to the build-time
   * `SUBSIDIARY_CODE`; overridable so a future host can request a specific
   * tenant surface. Ignored for the `authenticated` source.
   */
  subsidiary?: string;
}

/**
 * Hook for fetching a Mapbox access token from the server.
 *
 * Back-compat: called as `useMapboxToken(true|false)` it behaves exactly as
 * before (authenticated admin token, `enabled` toggling the fetch). Called with
 * an options object it can additionally source the unauthenticated public token.
 *
 * @param optionsOrEnabled - a boolean `enabled` flag (legacy form) OR an options object.
 */
export function useMapboxToken(
  optionsOrEnabled: boolean | UseMapboxTokenOptions = true,
): UseMapboxTokenReturn {
  const options: UseMapboxTokenOptions =
    typeof optionsOrEnabled === 'boolean'
      ? { enabled: optionsOrEnabled }
      : optionsOrEnabled;

  const enabled = options.enabled ?? true;
  const source = options.source ?? 'authenticated';
  const subsidiary = options.subsidiary ?? SUBSIDIARY_CODE;

  const [token, setToken] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const fetchToken = async () => {
      try {
        const response =
          source === 'public'
            ? // Public donor-page path: unauthenticated, subsidiary in the route.
              await fetch(`${API_BASE_URL}/api/public/mapbox-token/${subsidiary}`)
            : // Authenticated admin path: bearer + subsidiary header.
              await fetch(`${API_BASE_URL}/api/configuration/mapbox-token`, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
                  'X-Subsidiary': SUBSIDIARY_CODE,
                },
              });

        if (response.ok) {
          const data: MapboxTokenDto = await response.json();
          setToken(data.token);
          setError(null);
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('Error loading Mapbox configuration:', errorData);
          setError('Failed to load Mapbox configuration');
        }
      } catch (err) {
        console.error('Error fetching Mapbox token:', err);
        setError('Failed to load Mapbox configuration');
      } finally {
        setIsLoading(false);
      }
    };

    fetchToken();
  }, [enabled, source, subsidiary]);

  return {
    token,
    isLoading,
    error,
  };
}

export default useMapboxToken;
